import type { Hallazgo } from '../../api';
import { PASOS, type Operacion } from './pasos';

/**
 * Lo que se escaneó con la barra de arriba, convertido en las operaciones de
 * Operar que tienen sentido para eso. Cada opción lleva los códigos que la
 * operación va a usar, en orden: Operar los consume cuando llega su paso.
 */
export type OpcionOperar = { op: Operacion; texto: string; codigos: string[] };

export function opcionesDeEscaneo(h: Hallazgo): OpcionOperar[] {
  const o = (op: Operacion, texto: string, ...codigos: string[]): OpcionOperar => ({ op, texto, codigos });
  if (h.tipo === 'recepcion') return [o('recibir', 'Recibir', h.codigo)];

  if (h.tipo === 'pallet') {
    if (h.estado === 'Despachado') return [];
    const completo = h.tipoPallet === 'completo';
    const egresar = completo && h.salida ? [o('egresar', 'Egresar', h.codigo)] : [];
    if (!h.posicion) return [o('ubicar', 'Ubicar', h.codigo), ...egresar];
    if (h.esEstacion) return [o('armar', 'Armar capachos', `E-${h.posicion}`, h.codigo), o('mover', 'Devolver a rack', h.codigo)];
    return [o('mover', 'Mover', h.codigo), ...(completo ? egresar : [o('fraccionar', 'Llevar a fraccionamiento', h.codigo)])];
  }

  if (h.tipo === 'capacho') return h.estado === 'Aceptado' ? [o('egresar', 'Egresar', h.codigo)] : [];

  // La zona de piso tiene muchos pallets: se elige cuál al escanearlo.
  if (h.tipo === 'ubicacion' && h.esZona) return h.ocupantes?.length ? [o('mover', 'Mover desde acá', h.codigo)] : [];
  if (h.tipo === 'ubicacion' && h.ocupante) {
    const mover = o('mover', 'Mover desde acá', h.codigo);
    return h.esEstacion ? [o('armar', 'Armar capachos', h.codigo), mover] : [mover];
  }
  return [];
}

/** Por qué no se ofrece operar, cuando el detalle no lo dice. */
export function avisoDeEscaneo(h: Hallazgo): string | null {
  if (h.tipo === 'pallet' && h.estado === 'Despachado') return 'Despachado: ya salió del depósito.';
  if (h.tipo === 'capacho' && h.estado === 'Entregado') return 'Ya egresado.';
  if (h.tipo === 'capacho' && h.estado === 'Pedido') return 'En Pedido: todavía no se aceptó contra un pallet.';
  return null;
}

/** El id de la ruta `#/operar/…`: la operación y sus códigos, separados por `~`. */
export const rutaOperar = (x: Pick<OpcionOperar, 'op' | 'codigos'>) => [x.op, ...x.codigos].join('~');

export function leerRutaOperar(id: string | null | undefined): { op: Operacion; codigos: string[] } | null {
  const [op, ...codigos] = (id ?? '').split('~');
  return Object.keys(PASOS).includes(op!) ? { op: op as Operacion, codigos: codigos.filter(Boolean) } : null;
}
